// vertragsMaske.ts — welche Vertragsmaske eine Vertragsart öffnet.
//
// Stand vorher als private Funktion in NewContractDialog.tsx; jetzt geteilt,
// weil auch das „+“-Menü im App-Header (NeuAnlageDialog) nach der Wahl der
// Vertragsart in dieselbe Maske springen muss. Eine zweite Kopie der Regel
// würde bei der nächsten Transport-Ausnahme auseinanderlaufen.

import type { Vertragsart } from "../../data/vertragskatalog";
import { TRANSPORT_VERTRAGSARTEN } from "../transportContractStep/sections";

export type VertragsMaske = "immobilie" | "kfz" | "transport" | "generic";

/**
 * Die fünf Vertragsarten, die die Transport-Vertragsmaske führt — benannt wie
 * im Vertragskatalog und in der Felddefinition. Die Liste steht hier und nicht
 * an `riskCategories`: „Fahrzeuginhaltsversicherung (Werkverkehr)" trägt auch
 * `kfz` und ginge sonst in die KFZ-Maske, „Reisegepäckversicherung" trägt gar
 * kein `transport` und ginge in die allgemeine.
 */
const TRANSPORT_ARTEN = new Set(TRANSPORT_VERTRAGSARTEN.map((v) => v.title));

export const kindForArt = (art: Vertragsart): VertragsMaske => {
  if (TRANSPORT_ARTEN.has(art.name)) return "transport";
  if (art.riskCategories.includes("immo")) return "immobilie";
  if (art.riskCategories.includes("kfz")) return "kfz";
  return "generic";
};

/**
 * Wohin die Anlage eines Vertrags für einen FESTEN Kontakt führt — dieselben
 * Ziele wie `goContactFlow` in NewContractDialog.tsx:
 *  - kfz        → KFZ-Vertragsfenster mit Kontakt
 *  - transport  → /vertrag/transport (hängt an keinem Risikoobjekt)
 *  - generic    → /vertrag/new mit Kontakt + Vertragsart (Allgemein-Maske,
 *                 Ertragsausfall/BU erkennt NewContractPage selbst)
 *  - immobilie  → braucht zusätzlich ein Immobilien-Risikoobjekt des Kontakts
 * Ohne Vertragsart („allgemeiner Vertrag") → Allgemein-Maske, die die Art
 * selbst abfragt.
 */
export type VertragsZiel =
  | { typ: "kfz-fenster" }
  | { typ: "route"; pfad: string; state: Record<string, unknown> }
  | { typ: "immobilie-risiko-waehlen" };

export const vertragsZiel = (
  art: Vertragsart | null,
  kategorieId: string | null,
  kontaktId: number,
): VertragsZiel => {
  if (!art) return { typ: "route", pfad: "/vertrag/new", state: { kontaktId } };
  const maske = kindForArt(art);
  if (maske === "kfz") return { typ: "kfz-fenster" };
  if (maske === "immobilie") return { typ: "immobilie-risiko-waehlen" };
  if (maske === "transport") return { typ: "route", pfad: "/vertrag/transport", state: { kontaktId } };
  return {
    typ: "route",
    pfad: "/vertrag/new",
    state: { kontaktId, artName: art.name, kategorieId },
  };
};
